import { colors } from 'constants/theme'

export type TdhTxpTableLayout = {
  minWidth: number
  code: string
  price: string
  date: string
  doctor: string
  duration: string
  status: string
  action: string
}

export type TreatmentPlanDocumentConfig = {
  titleKey: string
  showToothChart: boolean
  tableColumnCount: number
}

export type TreatmentPlanNoticeKey = 'TXP.PRINT.TABLE.NOTICE_EN' | 'TXP.PRINT.TABLE.NOTICE_TH'

export type TreatmentPlanPreviewLanguage = 'th' | 'en'

export const TREATMENT_PLAN_PREVIEW_LANGUAGE_STORAGE_KEY = 'TDH_TREATMENT_PLAN_PREVIEW_LANGUAGE'

export function paginateTdhTreatmentPlanItems<T>(items: T[]): T[][] {
  if (items.length <= 7) return [items]

  const firstPageSize = items.length > 10 ? 10 : 7
  const pages: T[][] = [items.slice(0, firstPageSize)]
  for (let index = firstPageSize; index < items.length; index += 15) {
    pages.push(items.slice(index, index + 15))
  }
  return pages
}

export function getTreatmentPlanLanguageButtonStyle() {
  return {
    minWidth: 92,
    minHeight: 32,
    px: 1.5,
    border: `1px solid ${colors.themeSecondColor30}`,
    borderRadius: '4px',
    textTransform: 'none' as const,
    fontSize: 14,
    fontWeight: 500,
    boxShadow: 'none',
    color: colors.themeSecondColor,
    backgroundColor: colors.white,
    '&:hover': {
      borderColor: colors.themeSecondDarkColor,
      color: colors.themeSecondDarkColor,
      backgroundColor: colors.themeSecondColor06,
    },
    '&:focus-visible': {
      outline: `2px solid ${colors.themeSecondColor}`,
      outlineOffset: 2,
    },
  }
}

export type TdhToothChartPreviewLayout = {
  columnsPerQuadrant: number
  rows: number
  aspectRatio: string
  previewHeight: number
  positionMarkSize: number
  positionMarkScale: number
  toothCellGridRows: string
  labelFontSize: number
}

export function getTdhToothChartPreviewLayout(): TdhToothChartPreviewLayout {
  return {
    columnsPerQuadrant: 8,
    rows: 2,
    aspectRatio: '2 / 1',
    previewHeight: 400,
    positionMarkSize: 32,
    positionMarkScale: 1,
    toothCellGridRows: 'minmax(0, 1fr) 32px 36px',
    labelFontSize: 12,
  }
}

type TreatmentPlanPreviewLanguageStorage = Pick<Storage, 'getItem' | 'setItem'>

export const TREATMENT_PLAN_ITEM_REMARK_MAX_LENGTH = 500

export function normalizeTreatmentPlanPreviewLanguage(value: any): TreatmentPlanPreviewLanguage {
  return `${value || ''}`.toLowerCase() === 'en' ? 'en' : 'th'
}

export function getTreatmentPlanPreviewLanguage(storage?: TreatmentPlanPreviewLanguageStorage): TreatmentPlanPreviewLanguage {
  if (!storage) return 'th'

  try {
    return normalizeTreatmentPlanPreviewLanguage(storage.getItem(TREATMENT_PLAN_PREVIEW_LANGUAGE_STORAGE_KEY))
  } catch (_) {
    return 'th'
  }
}

export function setTreatmentPlanPreviewLanguage(
  storage: TreatmentPlanPreviewLanguageStorage | undefined,
  language: any,
): TreatmentPlanPreviewLanguage {
  const normalizedLanguage = normalizeTreatmentPlanPreviewLanguage(language)

  try {
    storage?.setItem(TREATMENT_PLAN_PREVIEW_LANGUAGE_STORAGE_KEY, normalizedLanguage)
  } catch (_) {
    // Browser storage can be unavailable in private or restricted contexts.
  }

  return normalizedLanguage
}

export type TreatmentPlanPreviewTooth = {
  teeth: number
  isSelect: true
  positions: string[]
  [key: string]: any
}

export function getTreatmentPlanDocumentConfig(isTdhTxp: boolean): TreatmentPlanDocumentConfig {
  return isTdhTxp
    ? {
      titleKey: 'TXP.PRINT.TREATMENT_PLAN_ESTIMATE_TITLE',
      showToothChart: true,
      tableColumnCount: 4,
    }
    : {
      titleKey: 'TX_INFORMATION.BUTTON.PRINT_FORM',
      showToothChart: false,
      tableColumnCount: 8,
  }
}

export function getTreatmentPlanPrintTitle(documentTitle: string, documentReference?: string): string {
  const title = `${documentTitle || ''}`.trim()
  const reference = `${documentReference || ''}`
    .trim()
    .replace(/\s*\/\s*/g, ' - ')
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/\s+-\s+/g, ' - ')
    .replace(/^-+|-+$/g, '')

  return reference ? `${title} - ${reference}` : title
}

export function getTreatmentPlanDocumentItemRemark(_item: any): string {
  // Item-level additional information is for internal communication only.
  // Keep the document renderer's hook empty so no extra row content or spacing is printed.
  return ''
}

export function getTreatmentPlanItemRemarkCount(value: any): string {
  const remark = typeof value === 'string' ? value : `${value ?? ''}`
  return `${remark.length}/${TREATMENT_PLAN_ITEM_REMARK_MAX_LENGTH}`
}

export function getTreatmentPlanItemRemarkLabel(label: string, value: any): string {
  return `${label} (${getTreatmentPlanItemRemarkCount(value)})`
}

export function isTreatmentPlanItemRemarkTooLong(value: any): boolean {
  const remark = typeof value === 'string' ? value : `${value ?? ''}`
  return remark.length > TREATMENT_PLAN_ITEM_REMARK_MAX_LENGTH
}

export function getTreatmentPlanDocumentItemRemarkLabelKey(): string {
  return 'TXP.TABLE.REMARK'
}

export function getTreatmentPlanNoticeKey(language?: string): TreatmentPlanNoticeKey {
  return `${language || ''}`.toLowerCase().startsWith('en')
    ? 'TXP.PRINT.TABLE.NOTICE_EN'
    : 'TXP.PRINT.TABLE.NOTICE_TH'
}

export function getTreatmentPlanDocumentReference(plan: any): string {
  const normalizePart = (value: any): string => {
    const text = `${value ?? ''}`.trim()
    return text && text !== '-' ? text : ''
  }

  return [normalizePart(plan?.documentNumber), normalizePart(plan?.version)]
    .filter(Boolean)
    .join(' / ')
}

export function getTdhTreatmentPlanPrintStyle(): string {
  return '@page { size: A4 portrait !important; margin: 8mm !important; } @media print { html, body { width: 194mm; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } .tdh-plan-document { display: block !important; width: 194mm !important; max-width: 194mm !important; min-height: 0 !important; margin: 0 auto !important; padding: 0 !important; box-sizing: border-box !important; } .tdh-plan-document .tdh-document-page { position: relative !important; width: 194mm !important; max-width: 194mm !important; min-height: 280mm !important; margin: 0 !important; padding: 0 0 5mm !important; box-sizing: border-box !important; } .tdh-plan-document .tdh-document-page:not(:last-child) { break-after: page; page-break-after: always; } .tdh-plan-document .tdh-document-page .tdh-page-number { position: absolute !important; right: 0 !important; bottom: 0 !important; margin: 0 !important; } .tdh-plan-document .tdh-document-table-scroll { overflow: visible !important; } .tdh-plan-document .tdh-final-section, .tdh-plan-document .tdh-signatures { page-break-inside: avoid; break-inside: avoid; } }'
}

export function hasHorizontalOverflow(clientWidth: number, scrollWidth: number, tolerance = 1): boolean {
  return scrollWidth > clientWidth + tolerance
}

const normalizePreviewToothNumber = (value: any): number | null => {
  const toothNumber = `${value ?? ''}`.trim()
  if (!/^[1-8][1-8]$/.test(toothNumber)) return null
  return Number(toothNumber)
}

const flattenPreviewTeeth = (value: any): any[] => {
  if (!Array.isArray(value)) return value ? [value] : []
  return value.reduce((items: any[], entry: any) => items.concat(flattenPreviewTeeth(entry)), [])
}

const parsePreviewTeethJson = (value: any): any[] => {
  if (!value) return []
  if (typeof value !== 'string') return flattenPreviewTeeth(value)

  try {
    return flattenPreviewTeeth(JSON.parse(value))
  } catch (_) {
    return []
  }
}

export function getTreatmentPlanPreviewTeeth(items: any[] = []): TreatmentPlanPreviewTooth[] {
  const teethByNumber = new Map<number, TreatmentPlanPreviewTooth>()

  const addTooth = (tooth: any) => {
    if (tooth?.isSelect === false) return
    const teethNumber = normalizePreviewToothNumber(tooth?.teeth)
    if (teethNumber === null) return

    const positions = Array.isArray(tooth?.positions)
      ? tooth.positions
      : Array.isArray(tooth?.position) ? tooth.position : []
    const current = teethByNumber.get(teethNumber)
    const mergedPositions = Array.from(new Set([
      ...(current?.positions || []),
      ...positions.filter((position: any) => typeof position === 'string' && position),
    ]))

    teethByNumber.set(teethNumber, {
      ...(current || {}),
      ...tooth,
      teeth: teethNumber,
      isSelect: true,
      positions: mergedPositions,
    })
  }

  items.forEach((item: any) => {
    const savedTeeth = parsePreviewTeethJson(item?.teethJson)
    if (savedTeeth.length) {
      savedTeeth.forEach(addTooth)
      return
    }

    const fallbackTeeth = `${item?.teeth || ''}`.match(/\b[1-8][1-8]\b/g) || []
    fallbackTeeth.forEach((teethNumber) => addTooth({ teeth: teethNumber }))
  })

  return Array.from(teethByNumber.values())
}

export function getTxpPrintMenuConfig(isTdhTxp: boolean) {
  return {
    showPlanPrint: true,
    showTreatmentPlanForm: true,
    planPrintLabelKey: 'TX_INFORMATION.BUTTON.PRINT',
    treatmentPlanFormLabelKey: 'TX_INFORMATION.BUTTON.PRINT_FORM',
  }
}

export function getTreatmentPlanActionMenuConfig() {
  return {
    align: 'end' as const,
    popperConfig: { strategy: 'absolute' as const },
  }
}

export function isTdhTxpWideLayout(isTdh: boolean, width: number): boolean {
  // TDH Tx.P keeps one consistent horizontal table at every viewport width.
  // Narrow screens use the table's horizontal scroll instead of a second
  // stacked-row representation, so the main page and editor stay identical.
  return isTdh || width >= 1200
}

export function getTdhTxpTableMinWidth(isTdh: boolean, isWideLayout: boolean): number | undefined {
  if (!isTdh) return undefined
  return isWideLayout ? 1360 : 0
}

export function getTdhTxpTableLayout(isTdh: boolean, width: number): TdhTxpTableLayout | null {
  if (!isTdh || !isTdhTxpWideLayout(isTdh, width)) return null
  return {
    minWidth: 1200,
    code: '96px',
    price: '150px',
    date: '160px',
    doctor: '180px',
    duration: '110px',
    status: '120px',
    action: '72px',
  }
}
